export const REIMBURSEMENT_LOB_CODES = ['EI', 'CS', 'MR', 'EXC'] as const;

export type ReimbursementLobCode = (typeof REIMBURSEMENT_LOB_CODES)[number];

const normalizeCode = (value: unknown): string =>
  typeof value === 'string' ? value.trim().toUpperCase() : '';

const getRawSelection = (value: unknown): unknown[] => {
  if (Array.isArray(value)) return value;
  if (typeof value === 'string') return value.split(',');
  return [];
};

export const normalizeReimbursementLobSelection = (
  value: unknown,
  availableCodes: readonly string[] = REIMBURSEMENT_LOB_CODES
): string[] => {
  const selectedCodes = new Set(getRawSelection(value).map(normalizeCode).filter(Boolean));
  const authorizedCodes = availableCodes
    .map((code) => ({ raw: String(code).trim(), normalized: normalizeCode(code) }))
    .filter((code) => code.normalized);

  return authorizedCodes
    .filter((code, index, codes) =>
      selectedCodes.has(code.normalized) &&
      codes.findIndex((candidate) => candidate.normalized === code.normalized) === index
    )
    .map((code) => code.raw);
};

export const getReimbursementLobSelectionFromState = (
  state: Record<string, unknown>,
  availableCodes: readonly string[] = REIMBURSEMENT_LOB_CODES
): string[] => {
  const hasMultiSelectState = Object.prototype.hasOwnProperty.call(state, 'lineOfBusinessMultiSelect');
  const rawSelection = hasMultiSelectState
    ? state.lineOfBusinessMultiSelect
    : state.lineOfBusiness;

  return normalizeReimbursementLobSelection(rawSelection, availableCodes);
};

export const getReimbursementLobPayload = (
  selection: unknown,
  availableCodes: readonly string[] = REIMBURSEMENT_LOB_CODES
): string | undefined => {
  const normalizedSelection = normalizeReimbursementLobSelection(selection, availableCodes);
  return normalizedSelection.length > 0 ? normalizedSelection.join(',') : undefined;
};

export const getReimbursementLobPayloadFromState = (state: Record<string, unknown> = {}): string => {
  const selection = Object.prototype.hasOwnProperty.call(state, 'lineOfBusinessMultiSelect')
    ? state.lineOfBusinessMultiSelect
    : state.lineOfBusiness;

  return getReimbursementLobPayload(selection) || '';
};

export const getReimbursementLobCompatibilityValue = (
  selection: unknown,
  availableCodes: readonly string[] = REIMBURSEMENT_LOB_CODES
): string => {
  const normalizedSelection = normalizeReimbursementLobSelection(selection, availableCodes);
  return normalizedSelection.length === 1 ? normalizedSelection[0] : 'All';
};

export const persistReimbursementLobSelection = (
  selection: unknown,
  availableCodes: readonly string[] = REIMBURSEMENT_LOB_CODES
): string[] => {
  const normalizedSelection = normalizeReimbursementLobSelection(selection, availableCodes);
  const stateString = sessionStorage.getItem('state');
  const state = stateString ? JSON.parse(stateString) : {};

  state.lineOfBusinessMultiSelect = normalizedSelection;
  state.lineOfBusiness = getReimbursementLobCompatibilityValue(normalizedSelection);
  sessionStorage.setItem('state', JSON.stringify(state));

  return normalizedSelection;
};